import { HttpError, useCan, useCreate, useGo, useList, useTranslate, useUpdate, Link, CanAccess, useGetIdentity } from "@refinedev/core";
import { FaSearch } from "react-icons/fa";
import { FaChevronLeft } from "react-icons/fa6";
import { motion } from "framer-motion";

const containerVariants = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
};
import InfoCard from "@components/molecules/InfoCard";
import CircularButtonWithToggle, { State } from "@components/organisms/CircularButtonWithToggle";
import { FaLifeRing, FaCalendarDays, FaCalendarPlus } from "react-icons/fa6";
import { MdAssistWalker } from "react-icons/md";
import { IAvailability } from "interfaces";
import dayjs from "dayjs";
import { useAppCalendar } from "hooks/calendar/useAppCalendar";
import { useMemo } from "react";
import relativeTime from "dayjs/plugin/relativeTime";
import calendar from "dayjs/plugin/calendar";
import { Article } from "types/articles";
import { calendar_v3 } from "googleapis"
import { logger } from "@utils/logger";
import { ContentCard } from "@components/organisms/ContentCard";
import { EventCode, searchMetadataString } from "core/calendar/definitions";
import { AppointmentEvent } from "@components/atoms/AppointmentEvent";
import { MembershipPopup } from "@components/molecules/MembershipPopup";

dayjs.extend(relativeTime)
dayjs.extend(calendar)


type AssistanceType = 'helps' | 'assistance';

export const HomeProfessional = () => {
    const { mutate: mutateCreate } = useCreate();
    const { mutate: mutateUpdate } = useUpdate();
    const translate = useTranslate()
    const go = useGo()
    const { data: canUseHelps } = useCan({ action: 'show', resource: 'helps' })
    const { data: canUseAssistance } = useCan({ action: 'show', resource: 'assistance' })
    const { data: identity } = useGetIdentity<{ name?: string; display_name?: string }>();
    const userName = identity?.display_name?.split(' ')?.[0] || identity?.name?.split(" ")?.[0] || "";


    const getAvailability = (type: AssistanceType) => {
        return useList<IAvailability, HttpError, IAvailability>({
            resource: "availability",
            sorters: [
                {
                    field: 'updated_at',
                    order: 'desc',
                },
            ],
            filters: [
                {
                    field: "type",
                    operator: "eq",
                    value: type,
                },
            ],
            pagination: {
                current: 1,
                pageSize: 1,
            },
            meta: {
                fields: [
                    'id',
                    'status',
                    'type',
                    'updated_at',
                ],
            }
        });
    }

    const { data: dataHelps, isLoading: isLoadingHelps } = getAvailability('helps');
    const { data: dataAssistance, isLoading: isLoadingAssistance } = getAvailability('assistance');

    let lastHelpsStatus = null;
    let lastAssistanceStatus = null;

    if (!isLoadingHelps && !isLoadingAssistance) {
        const lastHelps = dataHelps?.data[0];
        const lastAssistance = dataAssistance?.data[0];
        lastHelpsStatus = lastHelps?.status === 'available' ? true : false;
        lastAssistanceStatus = lastAssistance?.status === 'available' ? true : false;
    }

    const createAvailability = (type: AssistanceType) => {
        mutateCreate({
            resource: "availability",
            values: {
                status: 'available',
                type: type,
            },
        });
    }

    const onChange = (status: State, lasts: IAvailability[] | undefined, type: AssistanceType) => {
        logger.debug(`Lasts ${type}`, lasts, status);
        const lastsIsArray = Array.isArray(lasts);
        const last = lastsIsArray ? lasts[0] : null;
        if (status === State.Available) {
            if (lastsIsArray && !last) {
                logger.debug(`Do not exists ${type}.`);
                createAvailability(type);
            } else if (last?.status === 'not-available') {
                logger.debug(`Already exists ${type}, and is Not Available, so create a new one.`);
                createAvailability(type);
            } else if (last?.status === 'available') {
                logger.debug(`Do not create a new ${type}, the last one is already available.`);
            } else {
                logger.debug(`Do not create a new ${type}, do not meet requirements.`);
            }
        } else {
            if (last?.status === 'available') {
                logger.debug(`Already exists ${type}, and is Available, so update it to not-available.`);
                mutateUpdate({
                    resource: "availability",
                    id: last?.id,
                    values: {
                        status: 'not-available'
                    },
                });
            } else {
                logger.debug(`Do not close existing ${type}, the last one is already not-available.`);
            }
        }
    }

    const onChangeHelps = (status: State) => {
        onChange(status, dataHelps?.data, 'helps');
    }

    const onChangeAssistance = (status: State) => {
        onChange(status, dataAssistance?.data, 'assistance');
    }


    /*
    * Appointments
    */
    const calendarId = useAppCalendar()
    // const calendarId = null

    const date = useMemo(() => dayjs(), [])
    const {
        data: events,
        isLoading: isLoadingAppoiments,
    } = useList<calendar_v3.Schema$Event, HttpError, calendar_v3.Schema$Event>({
        dataProviderName: 'gapi',
        resource: `calendar/calendars/:calendar/events`,
        meta: {
            params: {
                calendar: calendarId?.appointments
            }
        },
        queryOptions: {
            enabled: !!calendarId,
        },
        sorters: [
            {
                field: 'startTime',
                order: 'asc'
            }
        ],
        pagination: {
            pageSize: 1,
        },
        filters: [
            {
                field: 'timeMin',
                operator: 'eq',
                value: date.toISOString()
            },
            {
                field: 'showDeleted',
                operator: 'eq',
                value: false
            },
            {
                field: 'singleEvents',
                operator: 'eq',
                value: true
            },
            {
                field: 'q',
                operator: 'eq',
                value: searchMetadataString({ code: EventCode.APPOINTMENT })
            }
        ]
    })
    const followingEvent = events?.data[0]
    const followingDate = followingEvent ? dayjs(followingEvent.start!.dateTime) : null
    // logger.debug(followingEvent)

    /*
    * Content
    */
    const {
        data,
        isLoading: isLoadingArticles,
    } = useList<Article, HttpError>({
        resource: 'articles',
        sorters: [
            {
                field: 'published_at',
                order: 'desc'
            }
        ],
        pagination: {
            pageSize: 5,
        },
        filters: [
            {
                field: 'tags',
                operator: 'contains',
                value: 'pro'
            }
        ],
        meta: {
            fields: ['id', 'title', 'content', 'author_id', 'landing_image', 'tags', 'published_at', 'created_at', 'updated_at']
        }
    })

    const posts = data?.data

    // logger.debug("can use Helps", canUseHelps?.can)

    return (
        <CanAccess resource="home" action="show" params={{ validateSubscription: true }} fallback={<MembershipPopup />}>
            <div className="flex flex-col flex-1 w-full min-h-screen">
                {/* Top Header Section */}
                <header className="md:h-auto md:ml-24 relative z-10">
                    <div className="hidden md:block z-30 absolute top-0 -ml-20 left-0 w-20 h-8 bg-transparent rounded-tr-full shadow-[2rem_0_var(--color-secondary)]"></div>
                    <div className="w-full bg-secondary pb-24 md:pb-12 md:pl-12 pt-8 px-6 rounded-bl-[3rem] md:rounded-bl-[4rem] relative overflow-hidden shadow-xl h-48">
                        <div className="absolute inset-0 from-white/10 via-transparent to-black/10 pointer-events-none" />
                        <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
                        <img src="/img/hachi-corner-text-light.png" alt="hachimind logo" className="lg:hidden w-48 absolute bottom-0 right-0 delay-500 animate-appear-from-bottom opacity-50 mix-blend-overlay" />

                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative z-10">
                            <div className="flex flex-col">
                                <span className="text-secondary-content/70 text-sm font-semibold mb-1 tracking-widest uppercase">{translate("page.home.welcome", "Bienvenido")}</span>
                                <h1 className="text-3xl sm:text-4xl font-extrabold text-base-100 tracking-tight drop-shadow-sm">
                                    {userName
                                        ? translate("page.home.greeting", { name: userName })
                                        : translate("page.home.greeting.noName")}
                                </h1>
                            </div>
                            <div className="flex items-center gap-3 sm:gap-4 shrink-0">
                                <button className="p-3 sm:p-3.5 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md transition-all duration-300 border border-white/10 hover:scale-105 shadow-sm">
                                    <FaSearch className="w-5 h-5 text-base-100" />
                                </button>
                            </div>
                        </div>
                    </div>
                </header>

                <main className="flex-1 px-4 sm:px-6 md:px-8 pb-12 -mt-10 md:-mt-8 relative z-20 w-full max-w-[1600px] mx-auto md:w-[calc(100%-12rem)]">
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="show"
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 w-full"
                    >
                        {/* Activations Bento Box */}
                        <motion.div variants={itemVariants} className="col-span-1 md:col-span-2 lg:col-span-12">
                            <div className="bg-base-100/90 backdrop-blur-xl rounded-[2rem] p-6 sm:p-8 shadow-sm border border-base-200/60 shadow-inner">
                                <h2 className="text-lg font-bold text-base-content/90 mb-6">{translate("page.home.topButtons.activations")}</h2>
                                <div className="flex flex-wrap gap-6 sm:gap-8 justify-center sm:justify-start">
                                    <div className="group">
                                        <div className="transition-transform duration-300 group-hover:-translate-y-1">
                                            <CircularButtonWithToggle
                                                icon={FaLifeRing}
                                                className={isLoadingHelps ? 'skeleton shadow-sm' : 'shadow-sm'}
                                                text={translate("page.home.topButtons.help.title")}
                                                data-tip={!canUseHelps?.can ? translate("page.home.topButtons.helps.no-helps") : ''}
                                                delayedOnChangeState={onChangeHelps}
                                                lastStatus={lastHelpsStatus}
                                                disabled={!canUseHelps?.can}
                                            />
                                        </div>
                                    </div>
                                    <div className="group">
                                        <div className="transition-transform duration-300 group-hover:-translate-y-1">
                                            <CircularButtonWithToggle
                                                icon={MdAssistWalker}
                                                className={isLoadingAssistance ? 'skeleton shadow-sm' : 'shadow-sm'}
                                                text={translate("page.home.topButtons.assistance.title")}
                                                data-tip={!canUseAssistance?.can ? translate("page.home.topButtons.assistance.no-assistance") : ''}
                                                delayedOnChangeState={onChangeAssistance}
                                                lastStatus={lastAssistanceStatus}
                                                disabled={!canUseAssistance?.can}
                                            />
                                        </div>
                                    </div>
                                    <Link go={{
                                        to: { resource: "calendar", action: "list" },
                                        query: { 'create-event': true }
                                    }} className="flex items-center w-full sm:w-auto">
                                        <button
                                            data-tip={translate("page.home.topButtons.appointment.tip")}
                                            className="btn btn-primary h-14 w-full sm:w-auto rounded-xl shadow-[0_4px_20px_rgba(var(--color-primary),0.3)] hover:scale-[1.02] transition-transform flex items-center gap-2 px-6">
                                            <FaCalendarPlus className="w-5 h-5" />
                                            {translate("page.home.topButtons.appointment.title")}
                                        </button>
                                    </Link>
                                </div>
                            </div>
                        </motion.div>

                        {/* Appointments Bento Box */}
                        <motion.div variants={itemVariants} className="col-span-1 md:col-span-2 lg:col-span-5 flex flex-col">
                            <section className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-[2rem] p-6 sm:p-8 shadow-sm border border-base-200/60 h-full flex flex-col relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-3xl -mr-16 -mt-16 transition-transform duration-700 group-hover:scale-150 group-hover:bg-primary/10"></div>
                                <h2 className="text-xl font-bold text-base-content/90 mb-6 relative z-10">{translate("page.home.appointments")}</h2>
                                <div className="dropdown w-full relative z-10 flex-1 flex flex-col">
                                    <div tabIndex={0} role="button" className="w-full flex-1">
                                        <InfoCard
                                            className={isLoadingAppoiments ? 'skeleton shadow-sm' : 'shadow-sm border-none'}
                                            title={translate("page.home.next-appointment")}
                                            description={!isLoadingAppoiments ? (followingDate ? (
                                                <div className="flex items-center flex-wrap gap-2 mt-1">
                                                    <span className="capitalize font-medium">{`${followingDate.format('dddd, HH:mm')}`}</span>
                                                    <span className="badge badge-primary border-none shadow-sm">{`${date.to(followingDate)}`}</span>
                                                </div>
                                            ) : translate('page.home.no-following-appointment')) : '-'}
                                            icon={FaCalendarDays}
                                        />
                                    </div>
                                    {followingEvent && (
                                        <AppointmentEvent
                                            tabIndex={0}
                                            className="dropdown-content z-20 mt-2 w-full rounded-2xl shadow-xl border border-base-200 overflow-hidden"
                                            event={followingEvent}
                                        />
                                    )}
                                </div>
                            </section>
                        </motion.div>

                        {/* News / Knowledge Bento Box */}
                        <motion.div variants={itemVariants} className="col-span-1 md:col-span-2 lg:col-span-7">
                            <section className="bg-base-100/95 backdrop-blur-2xl rounded-[2rem] p-6 sm:p-8 shadow-sm border border-base-200/60 h-full">
                                <div className="flex items-center justify-between mb-8">
                                    <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary drop-shadow-sm">
                                        {`${translate("page.home.news")} / ${translate("page.home.knowledge")}`}
                                    </h2>
                                </div>
                                <div className="carousel carousel-center w-full gap-4 pb-4">
                                    {isLoadingArticles ? (
                                        Array.from({ length: 4 }).map((_, i) => (
                                            <div key={i} className="carousel-item w-[85%] sm:w-[50%] lg:w-[45%] xl:w-[40%]">
                                                <div className="h-[280px] w-full bg-base-200/50 rounded-3xl skeleton flex flex-col justify-end p-6 border border-base-200">
                                                    <div className="w-2/3 h-6 bg-base-300 rounded mb-2"></div>
                                                    <div className="w-1/2 h-4 bg-base-300 rounded"></div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (!posts || posts.length === 0) ? (
                                        <div className="w-full py-12 flex flex-col items-center justify-center text-center bg-base-200/20 rounded-3xl border border-dashed border-base-300 mx-2">
                                            <div className="text-4xl mb-3 opacity-30">📚</div>
                                            <h3 className="font-bold opacity-70">{translate("articles.home.empty", "Próximamente más contenido")}</h3>
                                            <p className="text-xs opacity-50 mt-1">{translate("articles.home.empty_desc", "Estamos preparando nuevos artículos para ti.")}</p>
                                        </div>
                                    ) : (
                                        posts.map(post => (
                                            <div key={post.id!} className="carousel-item w-[85%] sm:w-[50%] lg:w-[45%] xl:w-[40%] overflow-visible">
                                                <div className="w-full h-full transition-all duration-300 hover:-translate-y-2 hover:shadow-xl rounded-2xl group">
                                                    <ContentCard post={post} />
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </section>
                        </motion.div>
                    </motion.div>
                </main>
            </div>
        </CanAccess>
    )
}
